import { useState, useCallback, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import useGenerate from '../hooks/useGenerate'
import useGallery from '../hooks/useGallery'
import useOffline from '../hooks/useOffline'
import { getModelById } from '../lib/models'
import type { GenerateRequest, Generation } from '../lib/api'
import PromptBar from '../components/PromptBar'
import OfflineIndicator from '../components/OfflineIndicator'
import FullscreenModal from '../components/FullscreenModal'
import ChatPanel from '../components/ChatPanel'
import VideoPlayer from '../components/VideoPlayer'
import { useChatPanel } from '../lib/chatContext'
import { useTerminal } from '../lib/terminalContext'
import {
  modelCodename,
  providerCodename,
  ENCODER_CODENAME,
} from '../lib/labCodenames'

interface StudioRouteState {
  prompt?: string
  model?: string
}

// Clean-by-default Studio:
// - no persistent history grid (Gallery page owns that)
// - default state is the brand hero
// - after a generation, a single large "latest" canvas replaces the hero
// - next generation replaces the latest inline — no scrolling history
// - terminal theater strip sits above the PromptBar while typing/focused
export default function Studio() {
  const location = useLocation()
  const navigate = useNavigate()
  const routeState = location.state as StudioRouteState | null
  const [selectedModel, setSelectedModel] = useState(
    routeState?.model ?? 'flux-schnell',
  )
  const [prompt, setPrompt] = useState(routeState?.prompt ?? '')
  const [selectedGeneration, setSelectedGeneration] =
    useState<Generation | null>(null)
  // The single-item canvas shown inline on the Studio page. Null = hero.
  const [sessionLatest, setSessionLatest] = useState<Generation | null>(null)

  const { chatOpen, openChat, closeChat } = useChatPanel()
  const { isGenerating, error, generate } = useGenerate()
  // useGallery still runs so Gallery page has fresh data; Studio doesn't
  // display it directly anymore.
  const { addGeneration, deleteGeneration } = useGallery()
  const { isOnline } = useOffline()

  // Global terminal dock — Studio feeds it the live prompt/model state
  // plus fires runtime events during generation.
  const {
    setPromptPreview,
    setModelName,
    streamPreflight,
    pushLog,
    setStage,
    resetStatus,
  } = useTerminal()

  const model = getModelById(selectedModel)

  // Mirror the current prompt + model into the terminal so the script
  // preview stays live as the user types.
  useEffect(() => {
    setPromptPreview(prompt)
  }, [prompt, setPromptPreview])
  useEffect(() => {
    setModelName(model?.name ?? selectedModel)
  }, [model, selectedModel, setModelName])

  // Clear router state after consuming it so a page refresh doesn't re-prefill
  useEffect(() => {
    if (routeState?.prompt || routeState?.model) {
      navigate(location.pathname, { replace: true, state: null })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleGenerate = useCallback(
    async (submittedPrompt: string) => {
      const m = getModelById(selectedModel)
      if (!m) return

      const request: GenerateRequest = {
        model: selectedModel,
        prompt: submittedPrompt,
        type: m.type,
        width: m.default_width,
        height: m.default_height,
        steps: m.default_steps,
      }

      const skeleton: Generation = {
        id: `pending-${Date.now()}`,
        user_id: '',
        model: selectedModel,
        prompt: submittedPrompt,
        type: m.type,
        width: m.default_width,
        height: m.default_height,
        steps: m.default_steps,
        nsfw: false,
        status: 'generating',
        created_at: new Date().toISOString(),
      }

      // Show the in-progress canvas immediately — replaces whatever was there
      setSessionLatest(skeleton)

      // Terminal runtime: fake preflight sequence streams in, then we
      // flag 'diffuse' as running against the real inference call. All
      // visible identifiers are codenames so the terminal log never
      // reveals the actual provider / model / encoder.
      const tokens = submittedPrompt.trim().split(/\s+/).filter(Boolean).length
      const started = Date.now()
      const mCode = modelCodename(selectedModel)
      // Primary provider code is derived from the frontend's static
      // provider label on the selected model, which maps 1:1 to the
      // worker's first route. Fallback to "commons" for anything unknown.
      const primaryReal = (m.provider as string) === 'hf' ? 'hf' : 'replicate'
      const pCode = providerCodename(primaryReal)

      // Kick off the preflight — fire-and-forget so it doesn't block
      // the real request, but we still await the stage transitions.
      void streamPreflight({
        modelCode: mCode,
        providerCode: pCode,
        encoderCode: ENCODER_CODENAME,
        tokens,
      })
      setStage('diffuse', 'running')
      pushLog({
        kind: 'running',
        label: `fv.diffuse(steps=${m.default_steps})`,
        note: `lane=${pCode} model=${mCode}`,
      })

      const result = await generate(request)

      if (result) {
        setStage('diffuse', 'ok')
        pushLog({ kind: 'ok', label: 'fv.diffuse', note: 'frame resolved' })
        setStage('upload', 'running')
        pushLog({ kind: 'running', label: 'fv.shard.upload', note: 'streaming' })
        setStage('upload', 'ok')
        pushLog({ kind: 'ok', label: 'fv.shard.upload', note: 'stored' })
        pushLog({
          kind: 'done',
          label: 'fv.generate',
          note: `${Date.now() - started}ms`,
        })
        setSessionLatest(result)
        addGeneration(result)
      } else {
        setStage('diffuse', 'fail')
        pushLog({
          kind: 'fail',
          label: 'fv.diffuse',
          note: 'lane refused payload',
        })
        pushLog({
          kind: 'info',
          label: 'fv.halt',
          note: 'refund pending',
        })
        setSessionLatest({ ...skeleton, status: 'failed' })
        // Leave the rest of the status map as it was so the user can see
        // exactly where the pipeline died.
        setTimeout(() => resetStatus(), 6000)
      }
    },
    [selectedModel, generate, addGeneration, streamPreflight, pushLog, setStage, resetStatus],
  )

  const handleReuse = useCallback((g: Generation) => {
    setPrompt(g.prompt)
    setSelectedModel(g.model)
  }, [])

  const handleChatReuse = useCallback(
    (newPrompt: string, newModel: string) => {
      setPrompt(newPrompt)
      setSelectedModel(newModel)
      closeChat()
    },
    [closeChat],
  )

  const showHero = sessionLatest === null

  return (
    <div
      className={`flex flex-col h-full transition-[margin-right] duration-300 ${
        chatOpen ? 'md:mr-[420px]' : ''
      }`}
    >
      <OfflineIndicator />

      {/* Floating chat toggle — visible when panel closed */}
      {!chatOpen && (
        <button
          onClick={openChat}
          className="fixed bottom-32 right-4 z-30 flex items-center gap-2 px-4 py-3 rounded-full bg-neon text-bg-primary font-semibold shadow-2xl shadow-neon/30 hover:bg-neon-dim transition-all cursor-pointer group"
          title="Open AI art director"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 16.8l-6.2 4.5 2.4-7.4L2 9.4h7.6z" />
          </svg>
          <span className="text-sm">Art Director</span>
        </button>
      )}

      {error && (
        <div className="mx-4 mt-4 px-4 py-3 rounded-lg bg-error/10 border border-error/30 text-error text-sm">
          {error}
        </div>
      )}

      {/* Main canvas area — either hero or latest-item card */}
      <div className="flex-1 overflow-y-auto p-4">
        {showHero ? (
          <HeroCanvas />
        ) : (
          <LatestCanvas
            generation={sessionLatest!}
            onOpen={() => sessionLatest && sessionLatest.status === 'complete' && setSelectedGeneration(sessionLatest)}
          />
        )}
      </div>

      <PromptBar
        prompt={prompt}
        onPromptChange={setPrompt}
        selectedModelId={selectedModel}
        onModelChange={setSelectedModel}
        onGenerate={handleGenerate}
        isGenerating={isGenerating}
        disabled={!isOnline}
      />

      {selectedGeneration && (
        <FullscreenModal
          generation={selectedGeneration}
          onClose={() => setSelectedGeneration(null)}
          onDelete={(id) => {
            void deleteGeneration(id)
            if (sessionLatest?.id === id) setSessionLatest(null)
          }}
          onReuse={handleReuse}
        />
      )}

      <ChatPanel
        open={chatOpen}
        onClose={() => closeChat()}
        onOpenGeneration={setSelectedGeneration}
        onReusePrompt={handleChatReuse}
      />
    </div>
  )
}

// ─── Hero: clean default state ─────────────────────────────────────────────
function HeroCanvas() {
  return (
    <div className="crt-scanlines flex items-center justify-center h-full relative overflow-hidden">
      <div className="absolute inset-0 grid-backdrop pointer-events-none opacity-40" />
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{
          background:
            'radial-gradient(circle, rgba(57,255,20,0.10) 0%, rgba(57,255,20,0.04) 35%, transparent 70%)',
          filter: 'blur(40px)',
          animation: 'pulse-soft 4s ease-in-out infinite',
        }}
      />
      <div className="text-center space-y-5 relative z-10">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-neon/10 border border-neon/30 mb-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#39FF14"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 16.8l-6.2 4.5 2.4-7.4L2 9.4h7.6z" />
          </svg>
        </div>
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-neon/60">
          FRANKENVIBES / LAB
        </p>
        <h2 className="brand-wordmark text-4xl text-text-primary">
          Create something amazing
        </h2>
        <p className="text-text-secondary max-w-sm mx-auto leading-relaxed">
          Type a prompt below — or open the Art Director chat to refine ideas
          in conversation. Past generations live in Gallery.
        </p>
        <p className="text-[10px] font-mono text-text-muted pt-4">
          Press{' '}
          <kbd className="px-1.5 py-0.5 rounded border border-border text-text-secondary">
            ⌘K
          </kbd>{' '}
          for commands
        </p>
      </div>
    </div>
  )
}

// ─── Latest: single-item canvas replacing the hero after a generation ─────
function LatestCanvas({
  generation,
  onOpen,
}: {
  generation: Generation
  onOpen: () => void
}) {
  const isPending =
    generation.status === 'pending' || generation.status === 'generating'
  const isFailed = generation.status === 'failed'

  return (
    <div className="flex items-center justify-center h-full relative overflow-hidden">
      <div className="absolute inset-0 grid-backdrop pointer-events-none opacity-20" />
      <div className="relative z-10 w-full max-w-2xl space-y-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neon/60 text-center">
          / LATEST GENERATION
        </p>
        {/* Frank-eureka celebration mark — appears top-right of the canvas
            only when the generation has landed successfully */}
        {generation.status === 'complete' && (
          <img
            src="/frank-eureka.png"
            alt="Frank celebrating the finished image"
            className="absolute -top-2 -right-2 w-20 h-auto z-20 pointer-events-none select-none drop-shadow-[0_0_18px_rgba(57,255,20,0.5)] animate-in fade-in duration-500"
            draggable={false}
          />
        )}
        <div
          className={`group relative aspect-square rounded-2xl overflow-hidden border border-border bg-bg-card transition-all ${
            generation.status === 'complete'
              ? 'cursor-pointer hover:border-neon/50 neon-glow'
              : ''
          }`}
          onClick={() => generation.status === 'complete' && onOpen()}
        >
          {generation.status === 'complete' && generation.r2_url && (
            <>
              {generation.type === 'video' ? (
                <VideoPlayer
                  src={generation.r2_url}
                  autoPlay
                  controls={false}
                />
              ) : (
                <img
                  src={generation.r2_url}
                  alt={generation.prompt}
                  className="w-full h-full object-cover"
                />
              )}
            </>
          )}
          {isPending && (
            <>
              <div className="absolute inset-0 bg-bg-elevated/40" />
              <div className="scan-beam" />
              <div className="absolute bottom-3 left-0 right-0 text-center pointer-events-none">
                <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-neon/80">
                  Developing
                </span>
              </div>
            </>
          )}
          {isFailed && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center space-y-2">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-error/20">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="text-error"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </div>
                <p className="text-xs text-error font-semibold uppercase tracking-wider">
                  Failed
                </p>
                <p className="text-[11px] text-text-muted">Try again or rephrase</p>
              </div>
            </div>
          )}
        </div>

        <p className="text-[11px] font-mono text-text-muted text-center leading-relaxed px-4 max-w-xl mx-auto">
          {generation.prompt}
        </p>
      </div>
    </div>
  )
}
